import { Phone, MessageCircle, Mail, CheckCircle, FileCheck, Zap, Cable, Lightbulb, Wrench, Building2, Shield, Clock, Award, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { companyInfo } from '../mockData';

export const Pricing = () => {
  const smallWorks = [
    { item: 'Replace socket/switch', price: '£65' },
    { item: 'Replace light fitting', price: '£75' },
    { item: 'Install downlight', price: '£85 each' },
    { item: 'Extractor fan replacement', price: '£120–£160' },
    { item: 'Smoke alarm installation', price: '£85 each' },
    { item: 'Outdoor light installation', price: '£120+' }
  ];

  const eicrPricing = [
    { type: '1–2 Bed', price: '£140', note: 'Labour only' },
    { type: '3 Bed', price: '£170', note: 'Labour only' },
    { type: '4 Bed', price: '£200', note: 'Labour only' },
    { type: '5 Bed', price: '£240+', note: 'Labour only' },
    { type: 'HMO (up to 6 circuits)', price: 'From £200', note: 'Labour only' },
    { type: 'Each additional circuit', price: '£20', note: 'Labour only' }
  ];

  const consumerUnits = [
    { 
      type: 'Standard RCBO Board with Surge Protection', 
      price: 'From £550',
      subtitle: 'Starting from £550 for 5 circuit fuseboard'
    }
  ];

  const rewiring = [
    { type: '1–2 Bed Flat', price: '£4,950' },
    { type: '3 Bed House', price: '£6,050 – £7,150' },
    { type: '4 Bed House', price: '£7,150 – £7,700' },
    { type: '5 Bed House', price: '£8,250 – £9,350+' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Pricing Hero */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 text-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Transparent, Competitive Pricing Across Greater London
          </h1>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Fast, certified electrical work for landlords, letting agents & homeowners
          </p>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="bg-white rounded-lg px-6 py-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_landlord-safety-1/artifacts/s7t87tk8_ukas2.jpg"
                alt="NICEIC Approved Contractor & UKAS"
                className="h-16 w-auto"
              />
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
              <span className="text-yellow-400">★★★★★</span>
              <span className="font-semibold">5.0 Google Rated</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
              <CheckCircle className="h-5 w-5" />
              <span className="font-semibold">Fully Insured</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
              <FileCheck className="h-5 w-5" />
              <span className="font-semibold">PRS Approved</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="w-full sm:w-auto gap-2 bg-white text-blue-600 hover:bg-gray-100">
                <Phone className="h-5 w-5" />
                Call Now
              </Button>
            </a>
            <a href="https://wa.me/447711233685" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="w-full sm:w-auto gap-2 border-white text-white hover:bg-blue-700">
                <MessageCircle className="h-5 w-5" />
                WhatsApp Us
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="w-full sm:w-auto gap-2 border-white text-white hover:bg-blue-700">
                <Mail className="h-5 w-5" />
                Request a Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* EICR Pricing */}
      <section className="py-20 bg-white" id="eicr-pricing">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-semibold mb-4">
              <FileCheck className="h-5 w-5" />
              Most Popular
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              EICR Pricing – Landlord Safety Certificates
            </h2>
            <p className="text-lg text-gray-600">
              Fast, compliant electrical inspections for rental properties
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="shadow-lg border-blue-200">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {[
                    { type: '1–2 Bed', price: '£140' },
                    { type: '3 Bed', price: '£170' },
                    { type: '4 Bed', price: '£200' },
                    { type: '5 Bed', price: '£240+' },
                    { type: 'HMO (up to 6 circuits)', price: 'From £200' },
                    { type: 'Each additional circuit', price: '£20' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center pb-4 border-b border-gray-200 last:border-0">
                      <span className="text-lg text-gray-700 font-medium">{item.type}</span>
                      <span className="text-xl font-bold text-blue-600">{item.price}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">What's included:</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Digital report in 24–48 hours</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">NICEIC certification</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Clear explanations of observations</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Remedials quoted separately</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 bg-blue-50 rounded-lg p-6 text-center">
                  <p className="text-lg font-semibold text-gray-900 mb-4">Need an EICR fast?</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <a href={`tel:${companyInfo.phone}`}>
                      <Button className="gap-2 bg-blue-600 hover:bg-blue-700">
                        <Phone className="h-4 w-4" />
                        Call {companyInfo.phone}
                      </Button>
                    </a>
                    <a href="https://wa.me/447711233685" target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="gap-2">
                        <MessageCircle className="h-4 w-4" />
                        WhatsApp Us
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Consumer Unit Upgrades */}
      <section className="py-20 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Consumer Unit (Fuse Board) Upgrades
            </h2>
            <p className="text-lg text-gray-600">
              Modern RCBO boards with surge protection as standard
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Card className="border-blue-200 shadow-lg">
              <CardHeader className="bg-gradient-to-br from-blue-50 to-white text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-2xl mb-2">Standard RCBO Board with Surge Protection</CardTitle>
                <div className="text-4xl font-bold text-blue-600 mb-2">From £550</div>
                <CardDescription className="text-base">
                  Starting from £550 for 5 circuit fuseboard
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-gray-900 mb-4 text-center">Package includes:</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">New metal consumer unit</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">RCBO protection on all circuits</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">SPD (Surge Protection Device)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Full testing & certification</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Old board removal</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Building Control notification</span>
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600 text-center">
                    <strong>Labour + materials included.</strong> Price varies based on number of circuits. 
                    Final quote confirmed after inspection.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Rewiring Pricing */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              🔌 Full Rewire
            </h2>
            <p className="text-lg text-gray-600">
              Includes RCBO Consumer Unit + SPD, Testing & Certification
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="shadow-lg border-blue-200">
              <CardContent className="p-8">
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <span className="text-lg text-gray-700 font-medium">1–2 Bed Flat</span>
                    <span className="text-xl font-bold text-blue-600">£4,950</span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <span className="text-lg text-gray-700 font-medium">3 Bed House</span>
                    <span className="text-xl font-bold text-blue-600">£6,050 – £7,150</span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <span className="text-lg text-gray-700 font-medium">4 Bed House</span>
                    <span className="text-xl font-bold text-blue-600">£7,150 – £7,700</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-lg text-gray-700 font-medium">5 Bed House</span>
                    <span className="text-xl font-bold text-blue-600">£8,250 – £9,350+</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 mb-6 border border-gray-200">
                  <p className="text-sm text-gray-700 text-center">
                    <strong>Empty property, standard spec, white accessories, average access</strong>
                  </p>
                </div>

                <div className="bg-orange-50 rounded-lg p-6 border-2 border-orange-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    💼 Occupied Property Uplift
                  </h3>
                  <p className="text-gray-700">
                    Add <strong className="text-orange-600">£400 – £800</strong> depending on furniture moving, floor lifting complexity, and protection required.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="mt-8 text-center">
              <Link to="/rewiring">
                <Button size="lg" variant="outline" className="gap-2">
                  View Full Rewiring Details
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Fault Finding & Small Works */}
      <section className="py-20 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Fault Finding */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Fault Finding & Repairs
              </h2>
              <Card className="border-gray-200">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                      <span className="text-gray-700 font-medium">First Hour Diagnostic</span>
                      <span className="text-xl font-bold text-blue-600">£95</span>
                    </div>
                    <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                      <span className="text-gray-700 font-medium">Each Additional Hour</span>
                      <span className="text-xl font-bold text-blue-600">£75</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Emergency Same Day Callout</span>
                      <span className="text-xl font-bold text-blue-600">£140</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-6 italic">* Minimum booking charge applies</p>
                </CardContent>
              </Card>
            </div>

            {/* Small Works */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Small Electrical Works
              </h2>
              <Card className="border-gray-200">
                <CardContent className="p-6">
                  <div className="space-y-3">
                    {smallWorks.map((work, idx) => (
                      <div key={idx} className="flex justify-between items-center pb-3 border-b border-gray-100 last:border-0">
                        <span className="text-gray-700">{work.item}</span>
                        <span className="font-bold text-blue-600">{work.price}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Lighting */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Emergency Lighting & HMO Compliance
          </h2>
          <div className="space-y-4">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle>Emergency Light Test (per fitting)</CardTitle>
                <CardDescription className="text-2xl font-bold text-blue-600">£12</CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle>Annual Emergency Lighting Certificate</CardTitle>
                <CardDescription className="text-2xl font-bold text-blue-600">From £120</CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle>Full HMO Electrical Compliance Package</CardTitle>
                <CardDescription className="text-2xl font-bold text-blue-600">From £350</CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Portfolio Landlord Discounts */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <Building2 className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Portfolio Landlord Discounts
          </h2>
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <p className="text-3xl font-bold mb-2">5% OFF</p>
              <p className="text-blue-100">For 3+ properties</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <p className="text-3xl font-bold mb-2">10% OFF</p>
              <p className="text-blue-100">For 6+ properties</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mb-8 max-w-2xl mx-auto">
            <div className="flex items-center gap-2 justify-center">
              <CheckCircle className="h-5 w-5" />
              <span>Priority Scheduling</span>
            </div>
            <div className="flex items-center gap-2 justify-center">
              <CheckCircle className="h-5 w-5" />
              <span>Annual Compliance Reminders</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="w-full sm:w-auto gap-2 bg-white text-blue-600 hover:bg-gray-100">
                <Phone className="h-5 w-5" />
                Call to Discuss Portfolio Pricing
              </Button>
            </a>
            <a href="https://wa.me/447711233685" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="w-full sm:w-auto gap-2 border-white text-white hover:bg-blue-700">
                <MessageCircle className="h-5 w-5" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-gray-100 border-y border-gray-300">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-gray-900 mb-2">Minimum booking charge: £95</p>
          <p className="text-sm text-gray-600">
            All prices are guide prices. Final quotes depend on property size, number of circuits, access, 
            parking, and any defects found during inspection. Certificates issued on completion of testing and payment.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-left">Do you issue certificates?</AccordionTrigger>
              <AccordionContent>
                Yes — NICEIC certificates provided where applicable. All our work is certified to current BS 7671 regulations, 
                giving you the documentation you need for councils, letting agents, and compliance requirements.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-left">How fast can I get an EICR?</AccordionTrigger>
              <AccordionContent>
                Typically 24–48 hours depending on access and workload. We understand landlords need fast turnaround times, 
                so we prioritize EICR inspections. Call us to discuss urgent requirements.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-left">Are remedials included in the EICR price?</AccordionTrigger>
              <AccordionContent>
                No — remedial works are quoted separately after findings. This ensures transparent pricing. 
                We'll provide a clear, itemized quote for any necessary repairs identified during the inspection.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4">
              <AccordionTrigger className="text-left">Do you cover all of London?</AccordionTrigger>
              <AccordionContent>
                Yes — Greater London and surrounding areas. We serve all London boroughs including Enfield, Tottenham, 
                Haringey, Hackney, Islington, Waltham Forest, and more. Contact us to confirm coverage for your specific location.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5">
              <AccordionTrigger className="text-left">What payment methods do you accept?</AccordionTrigger>
              <AccordionContent>
                We accept bank transfer, cash, and card payments. Payment is due on completion of work and before 
                certificates are issued.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Book Crescent Electrical Ltd Today
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Fast, certified, landlord-ready electrical work across Greater London
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="w-full sm:w-auto gap-2 bg-blue-600 hover:bg-blue-700">
                <Phone className="h-5 w-5" />
                Call {companyInfo.phone}
              </Button>
            </a>
            <a href="https://wa.me/447711233685" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="w-full sm:w-auto gap-2">
                <MessageCircle className="h-5 w-5" />
                WhatsApp {companyInfo.phone}
              </Button>
            </a>
          </div>
          <p className="text-gray-600">
            📩 Email: <a href={`mailto:${companyInfo.email}`} className="text-blue-600 hover:underline">{companyInfo.email}</a>
          </p>
        </div>
      </section>
    </div>
  );
};
